/*
  NS V2 Explore System (sections/nb-ns-v2-explore-system.liquid)

  Every popup is already in the markup next to its hotspot -- this script only
  decides which one is open and where it sits. On hover-capable desktops a
  hotspot opens on hover and a click pins it open; on touch a tap toggles it.
  Below 750px the CSS turns the popups into a panel under the card that always
  shows one card, starting with the first: a tap on a circle or on the panel's
  prev / next arrows switches cards, and no positioning is done. It also adds .is-revealed
  once the artwork scrolls into view, which starts the ring / line / label
  reveal in the CSS.
*/
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-nb-ns-v2-es]';

  /* Grace period so the pointer can travel from a hotspot into its popup. */
  var HOVER_CLOSE_MS = 150;
  /* Space between the hotspot and the popup edge, and from the viewport. */
  var POPUP_GAP = 16;
  var VIEWPORT_MARGIN = 8;

  var hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  var mobileQuery = window.matchMedia('(max-width: 749px)');

  function init(root) {
    if (root.nbNsV2EsInit === true) {
      return;
    }
    root.nbNsV2EsInit = true;

    var card = root.querySelector('.nb-ns-v2-es__card');
    var stage = root.querySelector('[data-es-stage]');
    var hotspots = Array.prototype.slice.call(root.querySelectorAll('[data-es-hotspot]'));
    var popups = Array.prototype.slice.call(root.querySelectorAll('[data-es-popup]'));

    if (!stage || hotspots.length === 0) {
      return;
    }

    /* Callout reveal -- same trigger as assets/nbv2-media-banner.js. Watches the
       artwork rather than the section so the rings draw when they are in view. */
    var revealObserver = null;

    if (root.classList.contains('nb-ns-v2-es--animated') && !root.classList.contains('is-revealed')) {
      var reduced =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reduced || typeof IntersectionObserver === 'undefined') {
        root.classList.add('is-revealed');
      } else {
        var threshold = parseFloat(root.dataset.animationThreshold);
        if (isNaN(threshold) || threshold < 0 || threshold > 1) {
          threshold = 0.4;
        }

        revealObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) {
                return;
              }
              root.classList.add('is-revealed');
              revealObserver.disconnect();
              revealObserver = null;
            });
          },
          { threshold: threshold }
        );

        revealObserver.observe(stage);
      }
    }

    var openIndex = -1;
    var pinned = false;
    var closeTimer = null;

    function canHover() {
      return hoverQuery.matches && !mobileQuery.matches;
    }

    function clearCloseTimer() {
      if (closeTimer !== null) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
    }

    function position(index) {
      var popup = popups[index];
      var hotspot = hotspots[index];

      if (mobileQuery.matches) {
        popup.style.removeProperty('--nb-ns-v2-es-popup-left');
        popup.style.removeProperty('--nb-ns-v2-es-popup-top');
        return;
      }

      /* Popups sit outside the stage, so offsets are measured from whatever
         they are positioned against. */
      var originRect = (popup.offsetParent || root).getBoundingClientRect();
      var cardRect = (card || stage).getBoundingClientRect();
      var hotRect = hotspot.getBoundingClientRect();
      var popupWidth = popup.offsetWidth;
      var popupHeight = popup.offsetHeight;

      /* Centered on the hotspot, clamped to the card. */
      var left = hotRect.left + hotRect.width / 2 - originRect.left - popupWidth / 2;
      var minLeft = cardRect.left - originRect.left;
      var maxLeft = cardRect.right - originRect.left - popupWidth;
      left = Math.max(minLeft, Math.min(left, maxLeft));

      /* Above the hotspot by default; below when there is more room there. */
      var spaceAbove = hotRect.top - POPUP_GAP - VIEWPORT_MARGIN;
      var spaceBelow = window.innerHeight - hotRect.bottom - POPUP_GAP - VIEWPORT_MARGIN;
      var top;

      if (spaceAbove >= popupHeight || spaceAbove >= spaceBelow) {
        top = hotRect.top - originRect.top - POPUP_GAP - popupHeight;
      } else {
        top = hotRect.bottom - originRect.top + POPUP_GAP;
      }

      popup.style.setProperty('--nb-ns-v2-es-popup-left', left + 'px');
      popup.style.setProperty('--nb-ns-v2-es-popup-top', top + 'px');
    }

    function open(index, pin) {
      clearCloseTimer();
      pinned = pin === true;

      if (openIndex === index) {
        return;
      }
      if (openIndex !== -1) {
        close(false);
      }

      openIndex = index;
      position(index);

      hotspots[index].setAttribute('aria-expanded', 'true');
      popups[index].setAttribute('aria-hidden', 'false');
      popups[index].classList.add('is-open');
      root.classList.add('is-popup-open');

    }

    /* The phone panel always shows a card; the first one unless told otherwise. */
    function openDefault() {
      if (mobileQuery.matches && openIndex === -1 && popups.length > 0) {
        open(0, true);
      }
    }

    function close(returnFocus) {
      clearCloseTimer();

      if (openIndex === -1) {
        return;
      }

      var index = openIndex;
      openIndex = -1;
      pinned = false;

      hotspots[index].setAttribute('aria-expanded', 'false');
      popups[index].setAttribute('aria-hidden', 'true');
      popups[index].classList.remove('is-open');
      root.classList.remove('is-popup-open');

      if (returnFocus === true) {
        hotspots[index].focus();
      }
    }

    function scheduleClose() {
      if (pinned) {
        return;
      }
      clearCloseTimer();
      closeTimer = window.setTimeout(function () {
        closeTimer = null;
        close(false);
      }, HOVER_CLOSE_MS);
    }

    hotspots.forEach(function (hotspot, index) {
      hotspot.addEventListener('mouseenter', function () {
        if (canHover()) {
          open(index, pinned && openIndex === index);
        }
      });

      hotspot.addEventListener('mouseleave', function () {
        if (canHover()) {
          scheduleClose();
        }
      });

      hotspot.addEventListener('click', function (event) {
        event.preventDefault();

        if (mobileQuery.matches) {
          /* The panel stays open on phones; a tap only switches cards. */
          open(index, true);
        } else if (openIndex === index && (pinned || !canHover())) {
          close(false);
        } else {
          open(index, true);
        }
      });
    });

    popups.forEach(function (popup) {
      popup.addEventListener('mouseenter', function () {
        if (canHover()) {
          clearCloseTimer();
        }
      });

      popup.addEventListener('mouseleave', function () {
        if (canHover()) {
          scheduleClose();
        }
      });
    });

    /* Prev / next arrows in the phone panel. Each card carries its own pair, so
       focus moves to the same arrow in the card that just appeared. */
    function onArrowClick(event) {
      var arrow = event.target.closest('[data-es-prev], [data-es-next]');
      if (!arrow || openIndex === -1) {
        return;
      }

      /* Loops: next on the last card goes to the first, and the other way round. */
      var isNext = arrow.hasAttribute('data-es-next');
      var target = (openIndex + (isNext ? 1 : -1) + popups.length) % popups.length;

      open(target, true);

      var focusTarget = popups[target].querySelector(isNext ? '[data-es-next]' : '[data-es-prev]');
      if (focusTarget) {
        focusTarget.focus({ preventScroll: true });
      }
    }

    function onDocumentClick(event) {
      if (openIndex === -1 || mobileQuery.matches) {
        return;
      }
      if (popups[openIndex].contains(event.target) || hotspots[openIndex].contains(event.target)) {
        return;
      }
      close(false);
    }

    function onKeydown(event) {
      if (event.key === 'Escape' && openIndex !== -1 && !mobileQuery.matches) {
        close(true);
      }
    }

    function onResize() {
      if (openIndex !== -1) {
        position(openIndex);
      }
    }

    function onBreakpointChange() {
      close(false);
      openDefault();
    }

    function onBlockSelect(event) {
      var target = event.target;
      hotspots.forEach(function (hotspot, index) {
        if (hotspot === target || hotspot.dataset.blockId === (event.detail && event.detail.blockId)) {
          open(index, true);
        }
      });
    }

    function onBlockDeselect() {
      close(false);
      openDefault();
    }

    openDefault();

    document.addEventListener('click', onDocumentClick);
    root.addEventListener('click', onArrowClick);
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('resize', onResize);
    mobileQuery.addEventListener('change', onBreakpointChange);
    root.addEventListener('shopify:block:select', onBlockSelect);
    root.addEventListener('shopify:block:deselect', onBlockDeselect);

    root.nbNsV2EsDestroy = function () {
      close(false);
      if (revealObserver !== null) {
        revealObserver.disconnect();
        revealObserver = null;
      }
      document.removeEventListener('click', onDocumentClick);
      root.removeEventListener('click', onArrowClick);
      document.removeEventListener('keydown', onKeydown);
      window.removeEventListener('resize', onResize);
      mobileQuery.removeEventListener('change', onBreakpointChange);
      root.removeEventListener('shopify:block:select', onBlockSelect);
      root.removeEventListener('shopify:block:deselect', onBlockDeselect);
      root.nbNsV2EsInit = false;
      root.nbNsV2EsDestroy = null;
    };
  }

  function initAll(scope) {
    var container = scope || document;
    var roots = Array.prototype.slice.call(container.querySelectorAll(ROOT_SELECTOR));

    if (container.nodeType === 1 && container.matches && container.matches(ROOT_SELECTOR)) {
      roots.push(container);
    }

    roots.forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var roots = Array.prototype.slice.call(event.target.querySelectorAll(ROOT_SELECTOR));
    roots.forEach(function (root) {
      if (typeof root.nbNsV2EsDestroy === 'function') {
        root.nbNsV2EsDestroy();
      }
    });
  });
})();
